import Seo from "@/components/base/Seo";
import Breadcrumb from "@/components/base/Breadcrumb";
import { milestones, principles, stats, testimonials } from "@/mocks/content";
import { SITE } from "@/config/site";

const ABOUT_IMAGE =
  "https://readdy.ai/api/search-image?query=Dark%20moody%20editorial%20photo%20of%20a%20broadcast%20signal%20van%20parked%20on%20a%20rainy%20city%20street%20at%20night%2C%20camera%20tripod%20and%20monitor%20glowing%20warm%20amber%2C%20deep%20charcoal%20tones%2C%20cinematic%20documentary%20high%20detail%20photography&width=1200&height=800&seq=blzb-about-01&orientation=landscape";

const jsonLd = [
  {
    "@context": "https://schema.org",
    "@type": "AboutPage",
    name: "关于我们 - 爆料直播",
    url: `${SITE.url}/about`,
    inLanguage: "zh-CN",
    description:
      "了解爆料直播的品牌定位、内容原则、发展历程与联系方式：以现场实时画面和直播镜头追踪记录正在发生的事。",
    isPartOf: { "@type": "WebSite", url: SITE.url },
  },
  {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [
      { "@type": "ListItem", position: 1, name: "首页", item: `${SITE.url}/` },
      { "@type": "ListItem", position: 2, name: "关于我们", item: `${SITE.url}/about` },
    ],
  },
];

export default function About() {
  return (
    <>
      <Seo
        title="关于我们｜直播镜头追踪与内容原则 - 爆料直播"
        description="爆料直播是聚焦第一现场的直播资讯平台。这里介绍我们的品牌定位、内容原则、发展理念与联系方式，说明为什么我们坚持呈现现场实时画面本身，而不是替当事人下结论。"
        keywords="关于爆料直播,直播镜头追踪,内容原则,现场实时画面,爆料直播平台介绍"
        path="/about"
        jsonLd={jsonLd}
      />

      <section className="relative overflow-hidden border-b border-background-200/70 pt-16 md:pt-20">
        <div className="stage-grid absolute inset-0 opacity-60" aria-hidden="true" />
        <div className="relative w-full px-4 py-10 md:px-6 md:py-14">
          <Breadcrumb
            items={[{ label: "首页", to: "/" }, { label: "关于我们" }]}
          />
          <h1 className="mt-6 max-w-3xl font-heading text-2xl font-bold leading-snug text-foreground-950 md:text-4xl">
            关于爆料直播：把现场原貌完整交给你
          </h1>
          <p className="mt-4 max-w-2xl text-sm leading-relaxed text-foreground-600 md:text-base">
            {SITE.orgDesc}
            我们不生产结论，只负责让信号到场、让镜头在场、让声音被听见。
          </p>
        </div>
      </section>

      <section className="w-full bg-background-50 px-4 py-14 md:px-6 md:py-20">
        <div className="grid grid-cols-1 items-start gap-8 lg:grid-cols-12 lg:gap-12">
          <div className="lg:col-span-6">
            <div className="overflow-hidden rounded-lg border border-background-200/70">
              <img
                src={ABOUT_IMAGE}
                alt="爆料直播信号车在雨夜街头架设机位"
                title="爆料直播 - 现场信号车与机位架设"
                className="aspect-[3/2] w-full object-cover object-top"
              />
            </div>
          </div>
          <div className="lg:col-span-6">
            <p className="font-label text-xs uppercase tracking-[0.28em] text-primary-500">
              Our Story
            </p>
            <h2 className="mt-3 font-heading text-2xl font-bold text-foreground-950 md:text-3xl">
              从一辆信号车，到 30 余个城市片区
            </h2>
            <div className="mt-5 flex flex-col gap-4 text-sm leading-relaxed text-foreground-700">
              <p>
                {SITE.since} 年的一个暴雨夜，我们只有一辆改装信号车和一条回传链路。那晚积水漫过路口，
                我们把画面送出去之后才意识到：比起事后的文字描述，现场的实时画面更能让人看懂究竟发生了什么。
              </p>
              <p>
                这几年里，爆料直播把机位从城市主干道延伸到社区楼道、批发市场与山区林场。
                每一路信号都标注采集时间，每一次连麦都保留原声。我们更在意的不是第几个到场，
                而是镜头能不能一直开着。
              </p>
              <p>
                我们的内容覆盖突发现场、民生追踪、城市路况、事故直击、天气预警与现场连麦六个方向，
                由 {SITE.founder} 统一值守，尽量让画面与声音互相印证。
              </p>
            </div>
            <div className="mt-6 grid grid-cols-2 gap-4 border-t border-background-200/70 pt-6">
              {stats.map((item) => (
                <div key={item.label}>
                  <p className="font-label text-xl font-bold text-foreground-950">{item.value}</p>
                  <p className="mt-1 text-[12px] text-foreground-600">{item.label}</p>
                  <p className="mt-0.5 text-[11px] text-foreground-400">{item.sub}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      <section className="w-full border-y border-background-200/70 bg-background-100 px-4 py-14 md:px-6 md:py-20">
        <p className="font-label text-xs uppercase tracking-[0.28em] text-primary-500">
          Principles
        </p>
        <h2 className="mt-3 font-heading text-2xl font-bold text-foreground-950 md:text-3xl">
          内容原则：四条不做的事
        </h2>
        <p className="mt-3 max-w-2xl text-sm leading-relaxed text-foreground-600">
          这些原则决定了一路画面能不能被推送出来，也决定了我们如何对待镜头里的每一个人。
        </p>

        <div className="mt-8 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4 md:gap-5">
          {principles.map((item) => (
            <article
              key={item.index}
              className="flex flex-col gap-3 rounded-lg border border-background-200/70 bg-background-50 p-5"
            >
              <span className="font-label text-2xl font-bold text-primary-500">{item.index}</span>
              <h3 className="font-heading text-base font-bold text-foreground-950">{item.title}</h3>
              <p className="text-[13px] leading-relaxed text-foreground-600">{item.desc}</p>
            </article>
          ))}
        </div>
      </section>

      <section className="w-full bg-background-50 px-4 py-14 md:px-6 md:py-20">
        <div className="grid grid-cols-1 gap-8 lg:grid-cols-12 lg:gap-12">
          <div className="lg:col-span-5">
            <p className="font-label text-xs uppercase tracking-[0.28em] text-primary-500">
              Milestones
            </p>
            <h2 className="mt-3 font-heading text-2xl font-bold text-foreground-950 md:text-3xl">
              发展理念与关键节点
            </h2>
            <p className="mt-3 text-sm leading-relaxed text-foreground-600">
              我们相信现场证据的价值会随时间上升。因此每场直播都会保留时间戳与完整档案，
              让之后回看的人也能还原当时的情况。
            </p>
          </div>
          <ol className="lg:col-span-7 flex flex-col gap-6 border-l border-background-300/50 pl-6">
            {milestones.map((item) => (
              <li key={item.year} className="relative">
                <span
                  className="absolute -left-[1.9rem] top-1.5 h-2.5 w-2.5 rounded-full bg-accent-500"
                  aria-hidden="true"
                />
                <p className="font-label text-sm font-bold text-primary-500">{item.year}</p>
                <h3 className="mt-1 font-heading text-base font-bold text-foreground-950">
                  {item.title}
                </h3>
                <p className="mt-1.5 text-[13px] leading-relaxed text-foreground-600">{item.desc}</p>
              </li>
            ))}
          </ol>
        </div>
      </section>

      <section className="w-full border-y border-background-200/70 bg-background-100 px-4 py-14 md:px-6 md:py-20">
        <h2 className="font-heading text-2xl font-bold text-foreground-950 md:text-3xl">
          用户口碑
        </h2>
        <p className="mt-3 max-w-2xl text-sm leading-relaxed text-foreground-600">
          反馈来自长期观看直播、参与连麦的网友，内容保持原话，不做修饰。
        </p>
        <div className="mt-8 grid grid-cols-1 gap-4 sm:grid-cols-2 md:gap-5">
          {testimonials.map((item) => (
            <figure
              key={item.name}
              className="rounded-lg border border-background-200/70 bg-background-50 p-5 md:p-6"
            >
              <div className="flex items-center justify-between gap-3">
                <i className="ri-double-quotes-l text-2xl text-primary-500" aria-hidden="true" />
                <span className="rounded-md bg-secondary-100/80 px-2 py-0.5 text-[11px] font-medium text-secondary-900">
                  {item.tag}
                </span>
              </div>
              <blockquote className="mt-3 text-sm leading-relaxed text-foreground-800">
                {item.text}
              </blockquote>
              <figcaption className="mt-4 border-t border-background-200/70 pt-3 text-[12px] text-foreground-500">
                {item.name} · {item.role}
              </figcaption>
            </figure>
          ))}
        </div>
      </section>

      <section className="w-full bg-background-50 px-4 py-14 md:px-6 md:py-20">
        <div className="grid grid-cols-1 gap-8 rounded-lg border border-background-200/70 bg-background-100 p-6 lg:grid-cols-12 md:p-10">
          <div className="lg:col-span-7">
            <p className="font-label text-xs uppercase tracking-[0.28em] text-primary-500">
              Contact
            </p>
            <h2 className="mt-3 font-heading text-2xl font-bold text-foreground-950 md:text-3xl">
              联系我们
            </h2>
            <p className="mt-3 max-w-xl text-sm leading-relaxed text-foreground-600">
              内容合作、转载授权、更正反馈与现场投稿都通过邮箱受理。请在邮件中说明事由与可核实的信息，
              编辑组会在工作时段内答复。
            </p>
            <ul className="mt-6 flex flex-col gap-4">
              <li className="flex items-start gap-3">
                <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-md bg-primary-100/60 text-primary-500">
                  <i className="ri-mail-line text-lg" aria-hidden="true" />
                </span>
                <span>
                  <span className="block text-[12px] text-foreground-500">内容合作与授权</span>
                  <a
                    href={`mailto:${SITE.email}`}
                    className="cursor-pointer text-sm text-foreground-900 underline decoration-background-400 underline-offset-4 transition-colors hover:text-primary-500"
                  >
                    {SITE.email}
                  </a>
                </span>
              </li>
              <li className="flex items-start gap-3">
                <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-md bg-accent-100/60 text-accent-700">
                  <i className="ri-send-plane-line text-lg" aria-hidden="true" />
                </span>
                <span>
                  <span className="block text-[12px] text-foreground-500">现场爆料与投稿</span>
                  <a
                    href={`mailto:${SITE.tipEmail}`}
                    className="cursor-pointer text-sm text-foreground-900 underline decoration-background-400 underline-offset-4 transition-colors hover:text-primary-500"
                  >
                    {SITE.tipEmail}
                  </a>
                </span>
              </li>
              <li className="flex items-start gap-3">
                <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-md bg-secondary-100/70 text-secondary-700">
                  <i className="ri-time-line text-lg" aria-hidden="true" />
                </span>
                <span>
                  <span className="block text-[12px] text-foreground-500">值守时间</span>
                  <span className="text-sm text-foreground-900">{SITE.serviceHours}</span>
                </span>
              </li>
            </ul>
          </div>
          <div className="lg:col-span-5">
            <div className="flex h-full flex-col justify-center rounded-lg border border-background-200/70 bg-background-50 p-6">
              <h3 className="font-heading text-lg font-bold text-foreground-950">
                想让自己的现场被看见？
              </h3>
              <p className="mt-2 text-[13px] leading-relaxed text-foreground-600">
                如果你手上有一段现场画面，或者本人就在事发现场，欢迎直接来信说明时间、地点与拍摄者身份。
                核实通过后，我们可能邀请你进入直播间连麦。
              </p>
              <a
                href={`mailto:${SITE.tipEmail}`}
                className="mt-5 inline-flex cursor-pointer items-center justify-center gap-1.5 whitespace-nowrap rounded-md bg-primary-500 px-5 py-2.5 text-sm text-background-50 transition-colors hover:bg-primary-600"
              >
                <i className="ri-megaphone-line" aria-hidden="true" />
                提交现场线索
              </a>
            </div>
          </div>
        </div>
      </section>
    </>
  );
}